import React, {
  FC, useCallback, useEffect, useState,
} from 'react';

import classNames from 'classnames';
import getClassNames from 'src/common/utils/getClassNames';
import {
  Theme, Variant, themes, variants,
} from './types';
import styles from './CostBreakdown.module.scss';
import Icon from '../IconDS22';

type DetailsToggleEvent = React.MouseEvent<HTMLDetailsElement, MouseEvent>;

export type CostDetailsProps = {
  /** Formatted amount e.g. `$955.00` */
  amount: string;
  children: React.ReactNode;
  disableCollapse?: boolean;
  open?: boolean;
  theme?: Theme;
  title: string;
  subHeading?: React.ReactNode;
  variant?: Variant;
  /** withHeader prop uses `details` and `summary` html elements in the component.  */
  withHeader?: boolean;
  noPadding?: boolean;
};

const CostDetails: FC<CostDetailsProps> = ({
  amount,
  children,
  disableCollapse = false,
  open: initiallyOpen = true,
  theme = themes[0],
  title,
  variant = variants[0],
  withHeader = true,
  noPadding = false,
  subHeading,
}) => {
  const [open, setOpen] = useState(initiallyOpen);
  const toggleHandler = useCallback((event: DetailsToggleEvent) => {
    setOpen(event.currentTarget.open);
  }, []);

  useEffect(() => {
    setOpen(initiallyOpen);
  }, [initiallyOpen]);

  return (
    <>
      {withHeader && (
        <details
          className={getClassNames(styles, 'root', classNames(theme, variant, { disableCollapse }))}
          open={open}
          onToggle={toggleHandler}
        >
          <summary
            className={getClassNames(styles, 'summary', classNames(variant, { disableCollapse }))}
            tabIndex={disableCollapse ? -1 : 0}
          >
            <span className={getClassNames(styles, 'priceLabel', variant)}>
              {/* Render Icon for secondary variant only */}
              {variant === variants[1] && (
                <Icon
                  id={open ? 'caret-up' : 'caret-down'}
                  size="m"
                />
              )}
              {title}
              {subHeading}
            </span>
            <span className={styles.priceValue}>
              {amount}
            </span>
          </summary>
          <div className={getClassNames(styles, 'content', classNames(variant, { noPadding }))}>
            {children}
          </div>
        </details>
      )}
      {!withHeader && (
        <div className={getClassNames(styles, 'content', classNames(variant, { noPadding }))}>
          {children}
        </div>
      )}
    </>
  );
};

export default CostDetails;
